import { AgentRunEvent } from '@lucidtech/las-sdk-browser';
import { memo, useMemo } from 'react';
import { Focusable } from 'react-aria-components';

import { Text } from '@/components/core';
import { AlertFilledIcon, ExportIcon, SpinnerIcon } from '@/components/icons';
import { toActionId, useGetAction } from '@/hooks/api';
import { isNotConfiguredExport } from '@/utils';

import { Cell, IndentedDiv, Indicator } from './common';
import { EventTooltip, EventTooltipBodyProps } from './EventTooltip';

const IExportEventTooltipBody = ({ event }: EventTooltipBodyProps) => {
  const { data: action } = useGetAction(toActionId(event.actionId));

  const isWarning = useMemo(() => event.status === 'failed' && isNotConfiguredExport(action), [action, event.status]);

  const { content } = useMemo(() => {
    let content;
    switch (event.status) {
      case 'succeeded':
        if (event?.skipped) {
          content = (
            <div className="gap-sm flex flex-col">
              <div className="gap-sm flex items-center">
                <Indicator variant="warning" />
                <Text>No values were exported because this action isn’t configured yet.</Text>
              </div>
            </div>
          );
        } else {
          content = (
            <div className="gap-sm flex flex-col">
              <div className="gap-sm flex items-center">
                <Indicator variant={'success'} />
                <Text>Values were exported to</Text>
              </div>
              <IndentedDiv>
                <Text>{action?.name}</Text>
              </IndentedDiv>
            </div>
          );
        }
        break;
      case 'failed':
        if (isWarning) {
          content = (
            <div className="gap-sm flex flex-col">
              <div className="gap-sm flex items-center">
                <Indicator variant="warning" />
                <Text>Integration not setup correctly</Text>
              </div>
              {event.warnings?.map((w, i) => (
                <IndentedDiv key={i}>
                  <Text>{w}</Text>
                </IndentedDiv>
              ))}
              {event.errors?.map((e, i) => (
                <IndentedDiv key={i}>
                  <Text>{e}</Text>
                </IndentedDiv>
              ))}
            </div>
          );
        } else {
          content = (
            <div className="gap-sm flex flex-col">
              <div className="gap-sm flex items-center">
                <Indicator variant="error" />
                <Text>Failed to export</Text>
              </div>
              <IndentedDiv>
                <Text>Something went wrong while exporting. Try again or contact support if the issue persists.</Text>
              </IndentedDiv>
              {event.warnings?.map((w, i) => (
                <IndentedDiv key={i}>
                  <Text>{w}</Text>
                </IndentedDiv>
              ))}
              {event.errors?.map((e, i) => (
                <IndentedDiv key={i}>
                  <Text>{e}</Text>
                </IndentedDiv>
              ))}
            </div>
          );
        }
        break;
      case 'running':
      case 'pending':
        content = (
          <div className="gap-sm flex flex-col">
            <Text>The AI agent is exporting the document and its values. This may take a few minutes.</Text>
          </div>
        );
        break;
      default:
        content = null;
    }

    return { content };
  }, [action, event, isWarning]);

  return content;
};

const ExportEventTooltipBody = memo(IExportEventTooltipBody) as typeof IExportEventTooltipBody;

export type ExportEventTooltipProps = {
  event: AgentRunEvent;
  agentId: string;
  runId: string;
};

const IExportEventTooltip = ({ event, agentId, runId }: ExportEventTooltipProps) => {
  const { data: action } = useGetAction(toActionId(event.actionId));

  const isWarning = useMemo(() => event.status === 'failed' && isNotConfiguredExport(action), [action, event.status]);

  const { heading } = useMemo(() => {
    let heading;
    switch (event.status) {
      case 'succeeded':
        if (event.skipped) {
          heading = 'Skipped';
        } else {
          heading = 'Successfully exported';
        }
        break;
      case 'failed':
        if (isWarning) {
          heading = 'Action required';
        } else {
          heading = 'Export failed';
        }
        break;
      case 'running':
      case 'pending':
        heading = 'Export in progress';
        break;
      default:
        heading = 'Unknown export status';
    }

    return { heading };
  }, [event.status, event.skipped, isWarning]);

  const icon = useMemo(
    () => (
      <Focusable>
        <Cell>
          {event.status === 'running' || event.status === 'pending' ? (
            <div className="relative">
              <ExportIcon className="opacity-50" functionId={action?.functionId} />
              <SpinnerIcon className="absolute right-0 bottom-0 z-50 size-4 translate-x-2.5 translate-y-2.5 rounded-full border border-gray-200 bg-white p-0.5 text-gray-900" />
            </div>
          ) : isWarning || event.skipped ? (
            <div className="relative">
              <ExportIcon functionId={action?.functionId} className="opacity-50" />
              <AlertFilledIcon className="text-warning-500 absolute right-0 bottom-0 z-50 size-3 translate-x-2.5 translate-y-2.5 rounded-full bg-white" />
            </div>
          ) : event.status === 'failed' ? (
            <div className="relative">
              <ExportIcon functionId={action?.functionId} />
              <AlertFilledIcon className="text-error-500 absolute right-0 bottom-0 z-50 size-3 translate-x-2.5 translate-y-2.5 rounded-full bg-white" />
            </div>
          ) : (
            <ExportIcon functionId={action?.functionId} />
          )}
        </Cell>
      </Focusable>
    ),
    [action?.functionId, event.status, event.skipped, isWarning]
  );

  return (
    <EventTooltip
      event={event}
      agentId={agentId}
      runId={runId}
      heading={heading}
      Body={ExportEventTooltipBody}
      timestamp={event.timestamp}
      icon={icon}
    />
  );
};

export const ExportEventTooltip = memo(IExportEventTooltip) as typeof IExportEventTooltip;
